import React, { useContext, useEffect, useState } from 'react';

import { RicosContext } from './../../ricos-context';
import type { GIFData } from './../../ricos-schema/next-schema';
import { GIFData_GIFType } from './../../ricos-schema/next-schema';
import { Image } from './../../ui-components';

import { ROOT } from '../data-hooks';
import classes from './giphy-viewer-scss';
import { PlayIcon } from './play-icon';
import { usePrefersReducedMotion } from './use-prefers-reduced-motion';

type Props = {
  componentData: GIFData;
  nodeId: string;
};

export const Giphy: React.FC<Props> = ({ componentData, nodeId }) => {
  const { width, height, gifType } = componentData;
  const { original } = componentData;
  const { tabIndex } = useContext(RicosContext);
  const isEditor = tabIndex === -1;
  const videoRef = React.useRef<HTMLVideoElement>(null);
  const prefersReducedMotion = usePrefersReducedMotion();
  const [isPlaying, setIsPlaying] = useState(() => !prefersReducedMotion);

  useEffect(() => {
    if (isEditor) {
      return;
    }
    setIsPlaying(!prefersReducedMotion);
    const video = videoRef.current;
    if (video && video.readyState >= HTMLMediaElement.HAVE_FUTURE_DATA) {
      prefersReducedMotion && isPlaying ? video.pause() : video.play();
    }
  }, [prefersReducedMotion]);

  const handleTogglePlay = () => {
    const video = videoRef.current;
    if (video) {
      video.paused ? video.play() : video.pause();
    }
    setIsPlaying(prev => !prev);
  };

  const videoSrc = original?.mp4;

  if (videoSrc && gifType !== GIFData_GIFType.STICKER) {
    return (
      <InteractiveGifContainer isPlaying={isPlaying} onTogglePlay={handleTogglePlay}>
        <video
          ref={videoRef}
          autoPlay={!prefersReducedMotion || isEditor}
          muted
          loop
          playsInline // required for autoplay in iOS
          data-hook={getGifDatahook(isPlaying)}
          className={classes.giphy_player}
          src={videoSrc}
          aria-label="gif"
        />
      </InteractiveGifContainer>
    );
  }

  const animatedSrc = !isPlaying && original?.still ? original.still : original?.gif;

  const isInfoForWixImageReady =
    nodeId !== undefined && width !== undefined && height !== undefined && animatedSrc;

  if (isInfoForWixImageReady) {
    return (
      <InteractiveGifContainer isPlaying={isPlaying} onTogglePlay={handleTogglePlay}>
        <Image
          id={nodeId}
          containerId={`${nodeId}-parent`}
          aria-label="gif"
          height={height}
          width={width}
          containerProps={{
            className: classes.gifRatio,
            containerId: `${nodeId}-parent`,
            style: {
              ['--dim-height' as string]: height,
              ['--dim-width' as string]: width,
            },
            dataHook: getGifDatahook(isPlaying),
          }}
          displayMode="fill"
          className={classes.giphy_player}
          uri={animatedSrc}
          alt="gif"
        />
      </InteractiveGifContainer>
    );
  }

  return (
    <InteractiveGifContainer isPlaying={isPlaying} onTogglePlay={handleTogglePlay}>
      <img
        aria-label="gif"
        className={classes.giphy_player}
        src={animatedSrc}
        alt="gif"
        data-hook={getGifDatahook(isPlaying)}
      />
    </InteractiveGifContainer>
  );
};

const getGifDatahook = (isPlaying: boolean) => `${ROOT} ${isPlaying ? 'playing' : 'paused'}`;

const InteractiveGifContainer: React.FC<{
  isPlaying: boolean;
  onTogglePlay: () => void;
  children: React.ReactNode;
}> = ({ isPlaying, onTogglePlay, children }) => {
  const { tabIndex } = useContext(RicosContext);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (tabIndex === -1) {
      return;
    }

    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      onTogglePlay();
    }
  };

  return (
    // eslint-disable-next-line jsx-a11y/no-static-element-interactions
    <div
      className={classes.giphyWrapper}
      tabIndex={tabIndex}
      onClick={() => {
        if (tabIndex === -1) {
          return;
        }
        onTogglePlay();
      }}
      onKeyDown={handleKeyDown}
      aria-label={isPlaying ? 'Pause gif' : 'Play gif'}
    >
      {children}
      {!isPlaying && (
        <span className={classes.playIcon}>
          <PlayIcon />
        </span>
      )}
    </div>
  );
};
